<template>
  <div class="vendors-container">
    <!-- Header -->
    <div class="page-header">
      <h1>Gestion des Fournisseurs</h1>
      <button
        class="btn btn-primary"
        @click="showCreateModal = true"
      >
        <i class="fas fa-plus" /> Nouveau Fournisseur
      </button>
    </div>

    <!-- Vendors Table -->
    <div class="table-container">
      <table
        v-if="vendors.length > 0"
        class="vendors-table"
      >
        <thead>
          <tr>
            <th>Nom</th>
            <th>Email</th>
            <th>Téléphone</th>
            <th>Mobile</th>
            <th>Site Web</th>
            <th>Dépenses</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="vendor in vendors"
            :key="vendor.id"
          >
            <td class="vendor-name">
              {{ vendor.name }}
            </td>
            <td>{{ vendor.email || '-' }}</td>
            <td>{{ vendor.work_phone || '-' }}</td>
            <td>{{ vendor.mobile || '-' }}</td>
            <td>
              <a
                v-if="vendor.website"
                :href="vendor.website"
                target="_blank"
                class="website-link"
              >
                <i class="fas fa-external-link-alt" /> Visiter
              </a>
              <span
                v-else
                class="text-muted"
              >-</span>
            </td>
            <td>
              <span class="badge">{{ vendor.expense_count || 0 }}</span>
            </td>
            <td class="actions-cell">
              <button
                class="btn-action btn-edit"
                title="Modifier"
                @click="editVendor(vendor)"
              >
                Modifier
              </button>
              <button
                class="btn-action btn-delete"
                title="Supprimer"
                @click="deleteVendor(vendor)"
              >
                Supprimer
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-else-if="loading"
        class="loading-state"
      >
        <i class="fas fa-spinner fa-spin" /> Chargement...
      </div>

      <div
        v-else
        class="empty-state"
      >
        <i class="fas fa-users" />
        <p>Aucun fournisseur trouvé</p>
        <button
          class="btn btn-primary"
          @click="showCreateModal = true"
        >
          <i class="fas fa-plus" /> Créer le premier fournisseur
        </button>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div
      v-if="showCreateModal || showEditModal"
      class="modal-overlay"
      @click.self="closeModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ showEditModal ? 'Modifier le Fournisseur' : 'Nouveau Fournisseur' }}</h2>
          <button
            class="btn-close"
            @click="closeModal"
          >
            <i class="fas fa-times" />
          </button>
        </div>

        <form
          class="modal-form"
          @submit.prevent="saveVendor"
        >
          <div class="form-group">
            <label class="required">Nom</label>
            <input
              v-model="form.name"
              type="text"
              required
            >
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Email</label>
              <input
                v-model="form.email"
                type="email"
              >
            </div>

            <div class="form-group">
              <label>Téléphone</label>
              <input
                v-model="form.work_phone"
                type="tel"
              >
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Mobile</label>
              <input
                v-model="form.mobile"
                type="tel"
              >
            </div>

            <div class="form-group">
              <label>Site Web</label>
              <input
                v-model="form.website"
                type="url"
                placeholder="https://"
              >
            </div>
          </div>

          <div class="form-group">
            <label>Adresse</label>
            <textarea
              v-model="form.address"
              rows="2"
            />
          </div>

          <div class="form-group">
            <label>Notes</label>
            <textarea
              v-model="form.notes"
              rows="3"
            />
          </div>

          <div class="modal-actions">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeModal"
            >
              <i class="fas fa-times" /> Annuler
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="btn btn-primary"
            >
              <i
                class="fas"
                :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"
              />
              {{ saving ? 'Enregistrement...' : 'Enregistrer' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'VendorsIndex',
  data() {
    return {
      vendors: [],
      loading: false,
      saving: false,
      showCreateModal: false,
      showEditModal: false,
      editingVendor: null,
      form: {
        name: '',
        email: '',
        work_phone: '',
        mobile: '',
        website: '',
        address: '',
        notes: '',
      },
    }
  },
  mounted() {
    this.loadVendors()
  },
  methods: {
    async loadVendors() {
      this.loading = true
      try {
        const response = await axios.get('/api/vendors/')
        this.vendors = response.data.results || response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading vendors:', error)
        this.$toast.error('Erreur lors du chargement des fournisseurs')
      } finally {
        this.loading = false
      }
    },

    editVendor(vendor) {
      this.editingVendor = vendor
      this.form = {
        name: vendor.name,
        email: vendor.email || '',
        work_phone: vendor.work_phone || '',
        mobile: vendor.mobile || '',
        website: vendor.website || '',
        address: vendor.address || '',
        notes: vendor.notes || '',
      }
      this.showEditModal = true
    },

    async saveVendor() {
      this.saving = true
      try {
        if (this.showEditModal) {
          await axios.put(`/api/vendors/update/${this.editingVendor.id}/`, this.form)
          this.$toast.success('Fournisseur modifié avec succès')
        } else {
          await axios.post('/api/vendors/create/', this.form)
          this.$toast.success('Fournisseur créé avec succès')
        }
        this.closeModal()
        this.loadVendors()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving vendor:', error)
        this.$toast.error('Erreur lors de l\'enregistrement')
      } finally {
        this.saving = false
      }
    },

    async deleteVendor(vendor) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Supprimer le fournisseur "${vendor.name}" ?`)) {
        return
      }

      try {
        await axios.delete(`/api/vendors/update/${vendor.id}/`)
        this.$toast.success('Fournisseur supprimé avec succès')
        this.loadVendors()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting vendor:', error)

        // Check if it's a protected error (vendor has related expenses)
        if (error.response && error.response.status === 500) {
          const errorMessage = error.response.data?.detail || error.response.data?.error || ''
          if (errorMessage.includes('protected') || errorMessage.includes('referenced')) {
            this.$toast.error(`Impossible de supprimer le fournisseur "${vendor.name}" car il est utilisé par ${vendor.expense_count} dépense(s). Veuillez d'abord supprimer ou réassigner ces dépenses.`)
          } else {
            this.$toast.error('Erreur lors de la suppression du fournisseur')
          }
        } else {
          this.$toast.error('Erreur lors de la suppression')
        }
      }
    },

    closeModal() {
      this.showCreateModal = false
      this.showEditModal = false
      this.editingVendor = null
      this.form = {
        name: '',
        email: '',
        work_phone: '',
        mobile: '',
        website: '',
        address: '',
        notes: '',
      }
    },
  },
}
</script>

<style scoped>
.vendors-container {
  padding: 20px;
  max-width: 100%;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.vendors-table {
  width: 100%;
  border-collapse: collapse;
}

.vendors-table thead {
  background: #f9fafb;
}

.vendors-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.vendors-table td {
  padding: 12px 16px;
  border-top: 1px solid #f3f4f6;
  font-size: 14px;
  color: #1f2937;
}

.vendors-table tbody tr:hover {
  background: #f9fafb;
}

.vendor-name {
  font-weight: 500;
  color: #1a202c;
}

.website-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 13px;
}

.website-link:hover {
  text-decoration: underline;
}

.badge {
  display: inline-block;
  padding: 4px 10px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.actions-cell {
  display: flex;
  gap: 8px;
}

.btn-action {
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.btn-action.btn-edit {
  background: #fef3c7;
  color: #92400e;
}

.btn-action.btn-edit:hover {
  background: #fde68a;
  color: #78350f;
}

.btn-action.btn-delete {
  background: #fee2e2;
  color: #dc2626;
}

.btn-action.btn-delete:hover {
  background: #fecaca;
  color: #991b1b;
}

.loading-state,
.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #d1d5db;
}

.empty-state p {
  margin-bottom: 20px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 95%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  background: linear-gradient(135deg, #0A2634 0%, #1a3a4a 100%);
  border-radius: 16px 16px 0 0;
}

.modal-header h2 {
  font-size: 22px;
  font-weight: 700;
  color: white;
  margin: 0;
}

.btn-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.25);
  color: white;
}

.modal-form {
  padding: 28px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.form-group label.required::after {
  content: ' *';
  color: #ef4444;
}

.form-group input,
.form-group textarea {
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.2s;
  background: #f8fafc;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #ff9f43;
  box-shadow: 0 0 0 4px rgba(255, 159, 67, 0.1);
  background: white;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #ff9f43 0%, #ff8c00 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff8c00 0%, #e67e00 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(255, 159, 67, 0.4);
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.text-muted {
  color: #9ca3af;
}
</style>
